import { useCallback, useEffect, useMemo, useState } from "react";
import { clsx } from "clsx";
import { UnitItemContext, type PromoExpandedBy } from "@/context/UnitItemContext";
import { UnitPromotions } from "@/components/units/unit/info/UnitPromotions";
import { UnitFeatures } from "@/components/units/unit/info/UnitFeatures";
import { UnitUrgency } from "@/components/units/unit/info/UnitUrgency";
import { UnitPrice } from "@/components/units/unit/info/UnitPrice";
import { UnitImage } from "@/components/units/unit/info/UnitImage";
import { NearbyCollapse } from "@/components/units/unit/info/NearbyCollapse";
import { FindNearbyButton } from "@/components/units/unit/info/FindNearbyButton";
import { PromoExpandedContent } from "@/components/units/unit/info/PromoExpandedContent";
import { InlineReservationForm } from "@/components/units/unit/info/InlineReservationForm";
import type { UnitGroup } from "@/types";
import { WidgetLayout } from "@/types/enums";
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { TriggerButtons } from "@/components/units/actions";
import { useUnitDataAttributes } from "@/hooks/useUnitDataAttributes";
import { useDisplayOptionContext } from "@/hooks/useDisplayOptionContext";
import { useSmoothScrollIntoView } from "@/hooks/useSmoothScrollIntoView";

interface UnitItemProps {
    unitGroup: UnitGroup;
    layout: "grid" | "row";
    isNearbyExpanded?: boolean;
    onNearbyToggle?: () => void;
}

export const UnavailableUnitItem = ({
    unitGroup,
    layout = "row",
    isNearbyExpanded = false,
    onNearbyToggle,
}: UnitItemProps) => {
    const { representative_unit, rates, available, show_nearby_units } =
        unitGroup;
    const { widgetLayout } = useDisplayOptionContext();
    const [selectedPromo, setSelectedPromo] = useState<number>(() => {
        if (rates.promo_rates.length >= 1) {
            // Non-null assertion - this will never be undefined.
            return rates.promo_rates[0]!.id;
        } else {
            return 0;
        }
    });

    // In-row promo/quote expansion state — mirrors UnitItem so the Layout 2
    // "Get a Quote" button (rendered by TriggerButtons for sold-out units)
    // actually toggles its inline form. Without this the QuoteButton's
    // setPromoExpandedBy call is a no-op and the button does nothing.
    const [promoExpandedBy, setPromoExpandedByState] =
        useState<PromoExpandedBy>(null);
    const dataAttributes = useUnitDataAttributes(unitGroup);

    // Opening the in-row quote/reserve expansion collapses the nearby panel so
    // only one expansion is open per row at a time. Closing (value === null)
    // leaves the nearby panel untouched.
    const setPromoExpandedBy = useCallback(
        (value: PromoExpandedBy) => {
            setPromoExpandedByState(value);
            if (value !== null && isNearbyExpanded) {
                onNearbyToggle?.();
            }
        },
        [isNearbyExpanded, onNearbyToggle],
    );

    // Opening the nearby panel collapses any open quote/reserve form, so the two
    // expansions never show at once. (isNearbyExpanded is still the pre-toggle
    // value here, so !isNearbyExpanded means we're about to open it.)
    const handleNearbyToggle = useCallback(() => {
        if (!isNearbyExpanded) {
            setPromoExpandedByState(null);
        }
        onNearbyToggle?.();
    }, [isNearbyExpanded, onNearbyToggle]);

    const isLayout2 = widgetLayout === WidgetLayout.LAYOUT_2;
    const hasMultiplePromos = rates.promo_rates.length > 1;
    const isFormExpanded =
        promoExpandedBy === "quote" || promoExpandedBy === "reserve";
    const canExpandRow = isLayout2 && (hasMultiplePromos || isFormExpanded);
    const [showActionForm, setShowActionForm] = useState(false);
    useEffect(() => {
        if (!isFormExpanded) setShowActionForm(false);
    }, [isFormExpanded]);
    const showFormStep =
        isFormExpanded && (!hasMultiplePromos || showActionForm);
    const promoExpandedPanelId = `promo-panel-${representative_unit.id}`;
    const rowExpansionRef = useSmoothScrollIntoView<HTMLDivElement>(
        promoExpandedBy !== null,
    );

    const unitItemInfo = useMemo(
        () => ({
            unitGroup,
            selectedPromo,
            setSelectedPromo,
            promoExpandedBy,
            setPromoExpandedBy,
            promoExpandedPanelId,
        }),
        [
            unitGroup,
            selectedPromo,
            promoExpandedBy,
            setPromoExpandedBy,
            promoExpandedPanelId,
        ],
    );

    const showNearby = show_nearby_units && layout === "row";

    const inner = (
        <>
            <div className="unit-item__image">
                <UnitImage
                    length={representative_unit.length}
                    width={representative_unit.width}
                    category={representative_unit.category}
                />
            </div>
            <div className="unit-item__dimension">
                {`${representative_unit.width}' x ${representative_unit.length}'`}
            </div>
            <div className="unit-item__category">
                {representative_unit.category?.name ?? ""}
            </div>
            <UnitFeatures features={representative_unit.features} />
            <div className="unit-item__promotions">
                <UnitPromotions
                    promotions={rates.promo_rates}
                    selectedPromo={selectedPromo}
                    onPromoChange={(promoId) => setSelectedPromo(promoId)}
                />
            </div>
            <div className="unit-item__urgency">
                <UnitUrgency available={available} />
            </div>
            <div className="unit-item__price">
                <UnitPrice
                    unitGroup={unitGroup}
                    selectedPromo={selectedPromo}
                />
            </div>
            <div className="unit-item__action">
                <TriggerButtons
                    unitGroup={unitGroup}
                    selectedPromo={selectedPromo}
                />
                {showNearby && (
                    <CollapsibleTrigger asChild>
                        <FindNearbyButton isExpanded={isNearbyExpanded} />
                    </CollapsibleTrigger>
                )}
            </div>
            {canExpandRow && (
                <div
                    ref={rowExpansionRef}
                    className="unit-item__promotions-expanded accordion-container"
                >
                    <Collapsible
                        open={promoExpandedBy !== null}
                        onOpenChange={(open) => {
                            if (!open) setPromoExpandedBy(null);
                        }}
                    >
                        <CollapsibleContent
                            className={clsx(
                                "promotion-selection",
                                showFormStep && "quote-selection",
                            )}
                            id={promoExpandedPanelId}
                        >
                            {showFormStep ? (
                                <InlineReservationForm
                                    mode={
                                        promoExpandedBy === "reserve"
                                            ? "reserve"
                                            : "lead"
                                    }
                                    unitGroup={unitGroup}
                                    selectedPromo={selectedPromo}
                                    onBack={
                                        hasMultiplePromos
                                            ? () => setShowActionForm(false)
                                            : undefined
                                    }
                                />
                            ) : (
                                <PromoExpandedContent
                                    promotions={rates.promo_rates}
                                    selectedPromo={selectedPromo}
                                    onPromoChange={setSelectedPromo}
                                    unitGroup={unitGroup}
                                    expandedBy={promoExpandedBy}
                                    onRequestQuoteForm={() =>
                                        setShowActionForm(true)
                                    }
                                    onRequestReserveForm={() =>
                                        setShowActionForm(true)
                                    }
                                />
                            )}
                        </CollapsibleContent>
                    </Collapsible>
                </div>
            )}
        </>
    );

    return (
        <UnitItemContext.Provider value={unitItemInfo}>
            {showNearby ? (
                <NearbyCollapse
                    isOpen={isNearbyExpanded}
                    onToggle={handleNearbyToggle}
                    className={clsx("unit-item", "unit-item--" + layout)}
                    dataAttributes={dataAttributes}
                >
                    {inner}
                </NearbyCollapse>
            ) : (
                <div
                    className={clsx("unit-item", "unit-item--" + layout)}
                    {...dataAttributes}
                >
                    {inner}
                </div>
            )}
        </UnitItemContext.Provider>
    );
};
